/**
 * Shared state for the map's layer controls: per-group and per-layer
 * visibility, per-group and per-layer opacity (effective opacity is the
 * PRODUCT of the two), and the swipe tool.
 */
import { atom } from 'jotai';
import {
  atomWithStorage,
  createJSONStorage,
  unstable_withStorageValidator as withStorageValidator,
} from 'jotai/utils';
import { EVENT_LEAF_KEYS, LAYER_REGISTRY } from './layerRegistry';

/** Persisted values come back from localStorage untrusted: clamp opacities to
 * [0,1], treating anything non-finite as fully opaque. Applied wherever a
 * persisted opacity is read (useLayerState, applyLayerOverrides). */
export const clamp01 = (n: number): number =>
  Number.isFinite(n) ? Math.min(Math.max(n, 0), 1) : 1;

/** Default swipe membership: every event leaf included, so enabling the swipe
 * tool with no further config swipes all event layers. */
const DEFAULT_SWIPE_LAYERS: Record<string, boolean> = Object.fromEntries(
  Array.from(EVENT_LEAF_KEYS, key => [key, true])
);

const LEAF_KEYS: ReadonlySet<string> = new Set(
  LAYER_REGISTRY.flatMap(g => g.children.map(l => l.key))
);
const GROUP_KEYS: ReadonlySet<string> = new Set(LAYER_REGISTRY.map(g => g.key));

/** localStorage content is untrusted (and can simply be stale after a
 * release): a record atom only accepts a plain object whose keys are current
 * registry keys and whose values pass the type guard. Anything else falls
 * back to the atom's default instead of crashing every consumer -- including
 * `__proto__`/junk keys, which would otherwise be persisted forever and read
 * back through the prototype chain. */
const recordOf =
  <T>(keys: ReadonlySet<string>, isValue: (v: unknown) => v is T) =>
  (v: unknown): v is Record<string, T> =>
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    Object.entries(v).every(([key, value]) => keys.has(key) && isValue(value));

const isBoolean = (v: unknown): v is boolean => typeof v === 'boolean';
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const storageOf = <T>(isValid: (v: unknown) => v is T) =>
  withStorageValidator(isValid)(createJSONStorage());

/** Per-layer visibility, keyed by registry leaf key. A missing entry means
 * visible -- only an explicit `false` hides. A layer renders only when both it
 * AND its group are visible; its own entry is kept while the group is off, so
 * re-enabling the group restores each layer's state. Persisted. */
export const leafVisibilityAtom = atomWithStorage<Record<string, boolean>>(
  'layerControls.visibility',
  {},
  storageOf(recordOf(LEAF_KEYS, isBoolean))
);

/** Per-group visibility, keyed by registry group key. Missing => visible.
 * Persisted. */
export const groupVisibilityAtom = atomWithStorage<Record<string, boolean>>(
  'layerControls.groupVisibility',
  {},
  storageOf(recordOf(GROUP_KEYS, isBoolean))
);

/** Per-group opacity (0..1), keyed by registry group key. Missing => 1.
 * Persisted. */
export const groupOpacityAtom = atomWithStorage<Record<string, number>>(
  'layerControls.groupOpacity',
  {},
  storageOf(recordOf(GROUP_KEYS, isFiniteNumber))
);

/** Per-layer opacity (0..1), keyed by leaf key. Missing => 1. The opacity a
 * layer renders at is `groupOpacity * leafOpacity`, so both sliders always
 * have an effect. Persisted. */
export const leafOpacityAtom = atomWithStorage<Record<string, number>>(
  'layerControls.opacity',
  {},
  storageOf(recordOf(LEAF_KEYS, isFiniteNumber))
);

/** Whether swipe MODE is active (ArcGIS-style: click-drag on the map reveals
 * the selected event layers along the drag axis). Ephemeral. */
export const swipeEnabledAtom = atom(false);

/** Swipe split state. `axis` is the locked drag axis; `coord` is the split
 * position ANCHORED TO THE MAP -- a longitude (horizontal axis) or latitude
 * (vertical axis), so panning/zooming the map never moves the split relative
 * to the data. `dir` is the locked drag direction along the axis (+1 = dragged
 * right/down, -1 = dragged left/up); the layer is hidden on the side the
 * cursor came FROM, so direction picks which side reveals the basemap. `null`
 * while the mode is on but no split exists yet. Ephemeral. */
export type SwipeDrag = { axis: 'horizontal' | 'vertical'; coord: number; dir: 1 | -1 };
export const swipeAtom = atom<SwipeDrag | null>(null);

/** Which event leaves the swipe clips, keyed by leaf key (missing => included).
 * Defaults to every event leaf -> true. Persisted. */
export const swipeLayersAtom = atomWithStorage<Record<string, boolean>>(
  'layerControls.swipeLayers',
  DEFAULT_SWIPE_LAYERS,
  storageOf(recordOf(EVENT_LEAF_KEYS, isBoolean))
);

/** Leaf keys that are actually present (rendered + not `visible:false`) on the
 * map right now, derived from the raw layer tree in HandleLayers. The UI shows
 * only these. Ephemeral -- recomputed from the live layers. */
export const presentLeavesAtom = atom<string[]>([]);
